import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Home, Grid3x3, ShoppingCart, PackageSearch, User, Bell, Search, LogOut, FileText, Wrench, X, PencilRuler, MessageSquare } from "lucide-react";
import * as CatIcons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import useUnreadMessages from "@/hooks/useUnreadMessages";
import InstallPWAButton from "@/components/InstallPWAButton";
import InstallBanner from "@/components/InstallBanner";
import OfflineIndicator from "@/components/OfflineIndicator";
import PendingOrderSync from "@/components/PendingOrderSync";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import logo from "@/assets/logo-togobati.png";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export default function ClientShell() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const msgUnread = useUnreadMessages();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const loadNotifs = useCallback(async () => {
    if (!user) return;
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {}
  }, [user]);

  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 30000); return () => clearInterval(t); }, [loadNotifs]);

  const unread = notifs.filter((n) => !n.read).length;

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/recherche?q=${encodeURIComponent(q)}`);
  };

  const markAll = async () => { try { await api.put("/notifications/read-all"); loadNotifs(); } catch {} };

  const nav = [
    { to: "/", icon: Home, label: "Accueil" },
    { to: "/recherche", icon: Grid3x3, label: "Catalogue" },
    { to: "/panier", icon: ShoppingCart, label: "Panier", badge: count },
    { to: "/commandes", icon: PackageSearch, label: "Suivi" },
    { to: "/compte", icon: User, label: "Compte" },
  ];

  return (
    <div className="min-h-screen bg-paper pb-20 md:pb-0">
      <OfflineIndicator />
      <PendingOrderSync />
      <InstallBanner />
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <img src={logo} alt="TOGOBATI" className="h-7 sm:h-9 w-auto" />
          </Link>
          <form onSubmit={submitSearch} className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un matériau..."
              className="w-full bg-slate-100 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/40"
              data-testid="header-search-input"
            />
          </form>
          <Link to="/panier" className="relative p-2 rounded-full hover:bg-slate-100 shrink-0 hidden md:flex" data-testid="header-cart">
            <ShoppingCart className="w-5 h-5 text-slate-600" />
            {count > 0 && <span className="absolute top-0.5 right-0.5 bg-[#C85A32] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center" data-testid="header-cart-count">{count}</span>}
          </Link>
          {user && (
            <Link to="/messages" className="relative p-2 rounded-full hover:bg-slate-100 shrink-0 flex" data-testid="header-messages">
              <MessageSquare className="w-5 h-5 text-slate-600" />
              {msgUnread > 0 && <span className="absolute top-0.5 right-0.5 bg-[#DC2626] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center" data-testid="header-messages-count">{msgUnread}</span>}
            </Link>
          )}
          <InstallPWAButton />
          <div className="relative hidden md:block">
            <button onClick={() => { setShowNotif((s) => !s); setShowMenu(false); }} className="relative p-2 rounded-full hover:bg-slate-100" data-testid="notif-bell">
              <Bell className="w-5 h-5 text-slate-600" />
              {unread > 0 && <span className="absolute top-1 right-1 bg-[#DC2626] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>}
            </button>
            {showNotif && (
              <div className="fixed left-3 right-3 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50" data-testid="notif-panel">
                <div className="flex items-center justify-between px-4 py-2.5 border-b">
                  <span className="font-semibold text-sm">Notifications</span>
                  <button onClick={markAll} className="text-xs text-[#C85A32] font-semibold" data-testid="notif-markall">Tout lire</button>
                </div>
                <div className="max-h-96 overflow-y-auto no-scrollbar">
                  {notifs.length === 0 && <p className="text-sm text-muted-foreground p-4 text-center">Aucune notification</p>}
                  {notifs.map((n) => {
                    const inner = (
                      <>
                        <p className="text-sm font-semibold text-slate-800">{n.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{formatDateTime(n.created_at)}</p>
                        {n.link && <p className="text-[11px] font-semibold text-[#C85A32] mt-1">Ouvrir →</p>}
                      </>
                    );
                    const base = `block px-4 py-3 border-b last:border-0 ${n.read ? "" : "bg-[#FDF3EE]"}`;
                    return n.link ? (
                      <Link key={n.id} to={n.link} onClick={() => setShowNotif(false)} className={`${base} hover:bg-slate-50 transition`} data-testid={`notif-${n.id}`}>{inner}</Link>
                    ) : (
                      <div key={n.id} className={base} data-testid={`notif-${n.id}`}>{inner}</div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {user ? (
            <div className="relative">
              <button onClick={() => { setShowMenu((s) => !s); setShowNotif(false); }} className="w-9 h-9 rounded-full bg-[#0F172A] text-white flex items-center justify-center font-semibold text-sm" data-testid="account-btn">
                {(user.name || "?").charAt(0).toUpperCase()}
              </button>
              {showMenu && (
                <div className="fixed left-3 right-3 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-52 bg-white rounded-xl shadow-2xl border border-slate-200 py-1 z-50" data-testid="account-menu">
                  <div className="px-4 py-2 border-b"><p className="text-sm font-semibold truncate">{user.name}</p><p className="text-xs text-slate-500 truncate">{user.email}</p></div>
                  <MenuLink to="/compte" icon={User} label="Mon profil" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/commandes" icon={PackageSearch} label="Mes commandes" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/devis" icon={FileText} label="Mes devis" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/projet" icon={PencilRuler} label="Votre projet" onClick={() => setShowMenu(false)} />
                  <MenuLink to="/messages" icon={MessageSquare} label={`Messagerie${msgUnread > 0 ? ` (${msgUnread})` : ""}`} onClick={() => setShowMenu(false)} />
                  <MenuLink to="/mes-installations" icon={Wrench} label="Mes installations" onClick={() => setShowMenu(false)} />
                  <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-slate-50" data-testid="logout-btn">
                    <LogOut className="w-4 h-4" /> Déconnexion
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="text-sm font-semibold bg-[#C85A32] text-white px-4 py-2 rounded-full hover:bg-[#A94520] transition whitespace-nowrap" data-testid="login-cta">Connexion</Link>
          )}
        </div>
      </header>

      {cats.length > 0 && (
        <nav className="bg-[#0F172A] text-slate-200 border-b border-slate-800" data-testid="category-navbar">
          <div className="max-w-6xl mx-auto px-2 sm:px-4 flex items-center gap-0.5 overflow-x-auto no-scrollbar">
            {cats.map((c) => {
              const CI = CatIcons[c.icon] || CatIcons.Package;
              return (
                <Link key={c.id} to={`/recherche?category=${c.id}`} data-testid={`navcat-${c.slug || c.id}`}
                  className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold whitespace-nowrap hover:text-white hover:bg-white/5 transition rounded">
                  <CI className="w-4 h-4 shrink-0" /> {c.name}
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      <main className="max-w-6xl mx-auto px-4 py-5 animate-fade-up" key={location.pathname}>
        <Outlet />
      </main>

      <SiteFooter />
      <WhatsAppFloat />

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-40 flex" data-testid="bottom-nav">
        {nav.map((n) => {
          const active = location.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} className="flex-1 flex flex-col items-center py-2 relative" data-testid={`bottomnav-${n.label.toLowerCase()}`}>
              <n.icon className={`w-5 h-5 ${active ? "text-[#C85A32]" : "text-slate-400"}`} />
              <span className={`text-[10px] mt-0.5 ${active ? "text-[#C85A32] font-semibold" : "text-slate-400"}`}>{n.label}</span>
              {n.badge > 0 && <span className="absolute top-1 right-1/4 bg-[#C85A32] text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{n.badge}</span>}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

function MenuLink({ to, icon: Icon, label, onClick }) {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
      <Icon className="w-4 h-4 text-slate-400" /> {label}
    </Link>
  );
}
